import * as React from 'react';

import { LicenseModel, FunctionModel } from '@app/Models/WebApiModels';

import DateTimeService from '@app/Services/DateTimeService';
import { ToolTipItem } from '@app/Components';

type LicenseProps = {
    licenses: LicenseModel[];
    currentFunction: FunctionModel;
    targetId?: string;
    selectedDate?: Date | null;
};

interface ILicenseModel {
    id: string;
    name: string;
    isExpired: boolean;
}

export class License extends React.Component<LicenseProps> {
    render() {
        const { licenses, currentFunction, targetId, selectedDate } = this.props;

        const sortedLicenses = licenses.filter((item) => item.visibleInRoster).sort((a, b) => a.sortOrder - b.sortOrder);

        const isNewcomer = currentFunction && currentFunction.isNewcomer;

        const expirationArray =
            (selectedDate &&
                licenses
                    .filter((item) => item.visibleInRoster && item.licenseExpirationDate && selectedDate.getUTCFullYear() === item.licenseExpirationDate.getUTCFullYear() && selectedDate.getUTCMonth() === item.licenseExpirationDate.getUTCMonth())
                    .sort((a, b) => a.sortOrder - b.sortOrder)
                    .map((item) => item.shortName)) ||
            [];

        const isNewcomerExperienced = (selectedDate && currentFunction && currentFunction.newcomerExperienceDate && DateTimeService.isAfterDate(selectedDate, currentFunction.newcomerExperienceDate)) || false;

        const typeRatingsLicenses: string[][] = [['A32', 'A33', 'A34', 'A33/4', 'A35', 'A34/5']];

        const cockpitQualificationLicenses: string[][] = [
            ['A32TRI', 'A33TRI', 'A34TRI', 'A35TRI'],
            ['A32TRIr', 'A33TRIr', 'A34TRIr', 'A35TRIr'],
            ['A32TRE', 'A33TRE', 'A34TRE', 'A35TRE'],
            ['A32TRE3', 'A33TRE3', 'A34TRE3', 'A35TRE3'],
            ['A32SFI', 'A33SFI', 'A34SFI', 'A35SFI'],
            ['A32SFE3', 'A33SFE3', 'A34SFE3', 'A35SFE3']
        ];

        const cabinCClassLicenses: string[] = ['A32C', 'A33/4C', 'A34/5C'];

        const typeRatingElements: ILicenseModel[] = this.combineLicensesToGroup(typeRatingsLicenses, sortedLicenses, selectedDate ? selectedDate : DateTimeService.today());
        const cockpitQualificationsElements: ILicenseModel[] = this.combineLicensesToGroup(cockpitQualificationLicenses, sortedLicenses, selectedDate ? selectedDate : DateTimeService.today());
        const bussinesClassQualificationsElements: ILicenseModel[] = this.combineBussinessClassLicenses(cabinCClassLicenses, sortedLicenses, selectedDate ? selectedDate : DateTimeService.today());

        const frontLicensesMaxSortOrder = 1000;
        const otherLicenses = sortedLicenses.filter((lic) => !Array.prototype.concat.apply([], typeRatingsLicenses).includes(lic.shortName) && !Array.prototype.concat.apply([], cockpitQualificationLicenses).includes(lic.shortName) && !cabinCClassLicenses.includes(lic.shortName));

        const otherLicensesFront = otherLicenses.filter((lic) => lic.sortOrder < frontLicensesMaxSortOrder);
        const otherLicensesEnd = otherLicenses.filter((lic) => lic.sortOrder >= frontLicensesMaxSortOrder);

        const otherLicensesFrontElements: ILicenseModel[] = otherLicensesFront.map((lic) => this.getLicenseModel(lic, (x: LicenseModel) => x.shortName, selectedDate ? selectedDate : DateTimeService.today()));
        const otherLicensesEndElements: ILicenseModel[] = otherLicensesEnd.map((lic) => this.getLicenseModel(lic, (x: LicenseModel) => x.shortName, selectedDate ? selectedDate : DateTimeService.today()));

        const traineeLicenses = this._getTraineeLicenses(sortedLicenses, typeRatingsLicenses);
        const isTraineeElements: ILicenseModel[] = [];
        if (traineeLicenses.length > 0) {
            traineeLicenses.forEach((l) => {
                isTraineeElements.push({
                    id: 'trainee_' + l.licenseId,
                    name: 'Trainee',
                    isExpired: (selectedDate && l.licenseExpirationDate && DateTimeService.isBeforeDate(l.licenseExpirationDate, selectedDate)) || false
                });
            });
        }
        if (isNewcomer && !isNewcomerExperienced) {
            isTraineeElements.push({
                id: 'newcomer_' + Date.now,
                name: 'Newcomer',
                isExpired: false
            });
        }

        const expired: string[] = [];
        if (selectedDate && expirationArray.length !== 0) {
            expired.push(`Expired in ${DateTimeService.toCustomUiFormat(selectedDate, 'MMMM YYYY')}`);
            expirationArray.map((item) => {
                expired.push(item);
            });
        }

        const hasAnyLicense = typeRatingElements.length > 0 || bussinesClassQualificationsElements.length > 0 || cockpitQualificationsElements.length > 0 || otherLicensesFrontElements.length > 0 || otherLicensesEndElements.length > 0;

        if (!hasAnyLicense) return null;
        else {
            const qualElements = bussinesClassQualificationsElements
                .concat(cockpitQualificationsElements)
                .concat(otherLicensesFrontElements)
                .filter((elem, index, self) => {
                    return self.map((mapObj) => mapObj['id']).indexOf(elem['id']) === index;
                });

            return (
                <div className="person-grid-person-crew">
                    {this.renderLicenseBlock(typeRatingElements, true)}
                    {qualElements.length > 0 ? ' - ' : ''}
                    {this.renderLicenseBlock(qualElements, true)}
                    {otherLicensesEndElements.length > 0 ? ' ' : ''}
                    {this.renderLicenseBlock(otherLicensesEndElements, true)}
                    {isTraineeElements.length > 0 ? ' - ' : ''}
                    {this.renderLicenseBlock(isTraineeElements, true)}
                    {targetId && (
                        <ToolTipItem targetId={targetId}>
                            {this.renderLicenseBlock(typeRatingElements, false)}
                            {qualElements.length > 0 ? '\r\n' : ''}
                            {this.renderLicenseBlock(qualElements, false)}
                            {otherLicensesEndElements.length > 0 ? ' ' : ''}
                            {this.renderLicenseBlock(otherLicensesEndElements, false)}
                            {isTraineeElements.length > 0 ? '\r\n' : ''}
                            {this.renderLicenseBlock(isTraineeElements, false)}
                            <br />
                            <span className="person-grid-license-expired">{expired.length > 0 && expired.join('\r\n')}</span>
                        </ToolTipItem>
                    )}
                </div>
            );
        }
    }

    renderLicenseBlock(arr: ILicenseModel[], isSetClass: boolean, ignoreExpiration: boolean = false): JSX.Element | null {
        if (arr.length === 0) return null;

        return (
            <>
                {arr.map((el, i) => {
                    const className = isSetClass && !ignoreExpiration && el.isExpired ? 'person-grid-license-expired-soon' : '';
                    return (
                        <span className={className} key={i}>
                            {el.name}
                            {el.name.endsWith('/') ? '' : ' '}
                        </span>
                    );
                })}
            </>
        );
    }

    getLicenseModel(lic: LicenseModel, getNameFunc: Function, selectedDate: Date | null): ILicenseModel {
        const isExpired = (selectedDate && lic.licenseExpirationDate && DateTimeService.isBeforeDate(lic.licenseExpirationDate, selectedDate)) || false;
        const licName = getNameFunc(lic);

        return {
            id: lic.id,
            name: licName,
            isExpired: isExpired
        };
    }

    combineLicensesToGroup(licenseGroups: string[][], sortedLicenses: LicenseModel[], selectedDate: Date | null): ILicenseModel[] {
        let elements: ILicenseModel[] = [];

        licenseGroups.forEach(
            (value) =>
            (elements = elements.concat(
                sortedLicenses
                    .filter((lic) => value.indexOf(lic.shortName) >= 0)
                    .sort((a, b) => +a - +b)
                    .map((lic, index, array) => {
                        let element: ILicenseModel;

                        if (array.length === 1) element = this.getLicenseModel(lic, (x: LicenseModel) => x.shortName, selectedDate);
                        //only one item
                        else if (index === 0) element = this.getLicenseModel(lic, (x: LicenseModel) => x.shortName.substr(0, 3) + '/', selectedDate);
                        //first of multiple items
                        else if (array.length === index + 1) element = this.getLicenseModel(lic, (x: LicenseModel) => x.shortName.substr(2), selectedDate);
                        //last of multiple items
                        else element = this.getLicenseModel(lic, (x: LicenseModel) => x.shortName.substr(2, 1) + '/', selectedDate); //any other items

                        return element;
                    })
            ))
        );

        return elements;
    }

    combineBussinessClassLicenses(licenseGroups: string[], sortedLicenses: LicenseModel[], selectedDate: Date | null): ILicenseModel[] {
        const elements: ILicenseModel[] = [];

        let applicableLicenses = [...sortedLicenses];
        const has345c = !!applicableLicenses.filter((lic) => 'A34/5C' === lic.shortName).length;
        if (has345c){
            applicableLicenses = applicableLicenses.filter((lic) => 'A33/4C' !== lic.shortName);
        }
        
        const toBeCombined = applicableLicenses.filter((lic) => licenseGroups.indexOf(lic.shortName) >= 0);
        if (toBeCombined.length === 2) elements.push(this.getLicenseModel(toBeCombined[0], () => 'C', selectedDate));
        else if (toBeCombined.length !== 0 && toBeCombined[0] && toBeCombined[0].shortName.trim()) elements.push(this.getLicenseModel(toBeCombined[0], (x: LicenseModel) => `C${x.shortName.substring(2, 3)}`, selectedDate));

        return elements;
    }

    private _getTraineeLicenses(sortedLicenses: LicenseModel[], licenseGroups: string[][]): LicenseModel[] {
        const licenseGroupsArray = licenseGroups.reduce((acc, val) => acc.concat(val), []);
        return sortedLicenses.filter((item) => item.isTrainee && licenseGroupsArray.includes(item.shortName));
    }
}
